import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import type { DriverRow } from "@/types/driver";
import type { DispatchItem } from "./dispatchItems";
import type { DispatchTableRow } from "./dispatchTableRow";
import type { DispatchGroup } from "./groupDispatchRows";
import { dispatchCoreKey, projectDispatchItemRecord, projectDispatchRecord, taskFields } from "./projectDispatchRecord";
import type { GroupedDispatchRows } from "./useDispatchQuery";

/** The group key of the shelf, which leads the table on every board while it holds a task. */
export const TASKS_GROUP_KEY = "_tasks";

/** The group key of every row the shelf leaves when the view groups nothing of its own. */
export const REST_GROUP_KEY = "_rest";

/**
 * The board's shelf as the filters leave it: its rows in shelf order, and the open tasks each row stands for, by row
 * id: what acknowledging that row closes.
 */
export type TaskShelf = {
  rows: readonly DispatchTableRow[];
  tasksByRow: ReadonlyMap<string, readonly OpenTask[]>;
};

/** One section before folding: a group's key, its header, and every row filed under it. */
type Section = { key: string; label: string; rows: DispatchTableRow[] };

/**
 * Lays the sections out as the table reads them: every group counted, the rows of the collapsed ones left out.
 *
 * @param sections - The groups in table order, each with all of its rows.
 * @param collapsed - The group keys folded away.
 */
export function foldShelfGroups(sections: readonly Section[], collapsed: ReadonlySet<string>): GroupedDispatchRows {
  const groups: DispatchGroup[] = [];
  const rows: DispatchTableRow[] = [];
  for (const section of sections) {
    const isCollapsed = collapsed.has(section.key);
    groups.push({ key: section.key, label: section.label, count: section.rows.length, collapsed: isCollapsed });
    if (!isCollapsed) rows.push(...section.rows);
  }
  return { groups, rows };
}

/**
 * The drivers an open task leads to, oldest task first, each once.
 */
function shelfDriverIds(shelf: readonly OpenTask[]): string[] {
  const ids = new Set<string>();
  for (const { task } of shelf) {
    for (const { workspaceMembershipId } of task.subjects) {
      if (workspaceMembershipId !== null) ids.add(workspaceMembershipId);
    }
  }
  return [...ids];
}

/**
 * The id a Dispatch item carries for a task's subject: the message's own, or `tmsEvent:<anchor>` for an event.
 */
function itemIdOf(task: OpenTask): string | null {
  for (const { subject, targetId } of task.task.subjects) {
    if (subject === "feed_item") return targetId;
    if (subject !== "membership") return `tmsEvent:${targetId}`;
  }
  return null;
}

/**
 * One task's shelf row at the message grain: the driver's fields, the subject item's message fields when it has one,
 * `item_kind` as `task`, and the three task kinds on both the driver and the message side.
 */
function taskRowOf(
  task: OpenTask,
  driver: DriverRow,
  item: DispatchItem | undefined,
  tagIdBySlug: ReadonlyMap<string, string>,
): DispatchTableRow {
  const base = projectDispatchRecord(driver, tagIdBySlug, true, task);
  const record = item ? projectDispatchItemRecord(base, driver, { ...item, task }) : { ...base };
  // A task waits until someone handles it, whatever its item's own log says.
  record[dispatchCoreKey("item_kind")] = "task";
  record[dispatchCoreKey("item_unacknowledged")] = "true";
  record[dispatchCoreKey("unacknowledged")] = "true";
  // A task about a driver has no item to carry the message twins, so the task fills them itself.
  if (!item) Object.assign(record, taskFields(task, "item_task_view", "item_task_author", "item_task_at"));
  return { kind: "task", id: `task:${task.task.id}`, task, driver, ...(item ? { item } : {}), record };
}

/**
 * The board's shelf rows that survive the filters the table's other rows pass, oldest task first.
 *
 * At the driver grain the shelf holds the shown driver rows an open task leads to, one per driver however many tasks,
 * so a driver the filters or the search hide takes their tasks off the shelf with them. At the message grain it holds
 * one `task` row per open task, about the driver the task bubbles up to and the message or event it names; its record
 * carries the driver's fields under the item's, so `keep`, the predicate the view applies to its own rows, hides it
 * exactly where it would hide that driver's messages. The per-grain row choice lives here alone, so the shelf's shape
 * changes in one place.
 */
export function shelveTasks({
  grain,
  shown,
  shelf,
  driverRowFor,
  itemFor,
  tagIdBySlug,
  keep,
}: {
  grain: DispatchGrain;
  /** The rows the view's filters, or the search in their place, keep. */
  shown: readonly DispatchTableRow[];
  /** The open tasks on this board's shelf, oldest first. */
  shelf: readonly OpenTask[];
  /** A driver by membership id, for a task row at the message grain; undefined for one the page does not list. */
  driverRowFor: (membershipId: string) => DriverRow | undefined;
  /** A Dispatch item by id, for the message or event a task row is about. */
  itemFor: (itemId: string) => DispatchItem | undefined;
  tagIdBySlug: ReadonlyMap<string, string>;
  /** The filter that made `shown`, applied to the task rows the message grain builds. */
  keep: (rows: DispatchTableRow[]) => DispatchTableRow[];
}): TaskShelf {
  const tasksByRow = new Map<string, readonly OpenTask[]>();
  if (grain === "driver") {
    // One shown driver row per driver a task leads to, so its record, count, and filtering are the table's own.
    const byDriver = new Map(shown.map((row) => [row.driver.workspaceMembershipId, row]));
    const rows = shelfDriverIds(shelf).flatMap((id) => byDriver.get(id) ?? []);
    // A driver row stands for this board's tasks about the driver, never for one pinned to another board.
    for (const row of rows) {
      const id = row.driver.workspaceMembershipId;
      tasksByRow.set(
        row.id,
        shelf.filter(({ task }) => task.subjects.some((subject) => subject.workspaceMembershipId === id)),
      );
    }
    return { rows, tasksByRow };
  }

  // One task row per task about a listed driver, then the view's own predicate over them.
  const built = shelf.flatMap((task) => {
    const driverId = task.task.subjects.find(
      (subject) => subject.workspaceMembershipId !== null,
    )?.workspaceMembershipId;
    const driver = driverId ? driverRowFor(driverId) : undefined;
    if (!driver) return [];
    const itemId = itemIdOf(task);
    return [taskRowOf(task, driver, itemId ? itemFor(itemId) : undefined, tagIdBySlug)];
  });
  const rows = keep(built);
  // A task row stands for its own task alone.
  for (const row of rows) {
    if (row.kind === "task") tasksByRow.set(row.id, [row.task]);
  }
  return { rows, tasksByRow };
}

/**
 * Pins a "Tasks" group holding the shelf first in the table, and lays the view's own groups after it.
 *
 * The view's own groups follow with the shelf's rows taken out, or one "Everything else" group when the view groups
 * nothing. At the message grain a message leaves them only when its task row made the shelf, so a filter that hides
 * the task row never hides the message too.
 *
 * @returns The groups and rows; null when the shelf is empty, so the table is laid out as the view alone lays it out.
 */
export function withTaskShelf({
  shelf,
  shown,
  groupRest,
  collapsed,
}: {
  /** The shelf {@link shelveTasks} built from `shown`. */
  shelf: TaskShelf;
  /** The rows the view's filters, or the search in their place, keep, in the view's order. */
  shown: readonly DispatchTableRow[];
  /** The view's own grouping of the rows the shelf leaves, or null when the view groups nothing. */
  groupRest: (rows: readonly DispatchTableRow[]) => GroupedDispatchRows | null;
  collapsed: ReadonlySet<string>;
}): GroupedDispatchRows | null {
  if (shelf.rows.length === 0) return null;

  // A driver row leaves the rest by its own id; a task row takes its message's row, keyed by the item id, with it.
  const shelved = new Set(shelf.rows.map((row) => (row.kind === "task" && row.item ? row.item.id : row.id)));
  const rest = shown.filter((row) => !shelved.has(row.id));

  const tasksSection: Section = { key: TASKS_GROUP_KEY, label: "Tasks", rows: [...shelf.rows] };
  const grouped = groupRest(rest);
  if (grouped === null) {
    return foldShelfGroups([tasksSection, { key: REST_GROUP_KEY, label: "Everything else", rows: rest }], collapsed);
  }
  const shelfPart = foldShelfGroups([tasksSection], collapsed);
  return { groups: [...shelfPart.groups, ...grouped.groups], rows: [...shelfPart.rows, ...grouped.rows] };
}
